"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AGENT_DELETE_LIMIT = exports.AGENT_BATCH_LIMIT = exports.attachAgentFileRequestSchema = exports.AGENT_LIBRARY_LIMIT = exports.agentLibraryFileListSchema = exports.agentLibraryFileSchema = exports.agentConversationFileListSchema = exports.agentConversationFileSchema = exports.reviseAgentProposalRequestSchema = exports.renameAgentConversationRequestSchema = exports.createAgentConversationRequestSchema = exports.sendAgentMessageRequestSchema = exports.AGENT_MESSAGE_MAX_CHARS = exports.agentConversationDetailSchema = exports.agentMessageSchema = exports.agentToolRunSchema = exports.AGENT_FRAMES = exports.agentFrameSchema = exports.storedAgentProposalSchema = exports.AGENT_PROPOSAL_STATES = exports.agentProposalSchema = exports.agentProposalRowSchema = exports.agentProposalFieldSchema = exports.AGENT_FIELD_KINDS = exports.agentExportOfferSchema = exports.agentDataBlockSchema = exports.AGENT_DATA_KINDS = exports.agentConversationSchema = exports.AGENT_ERROR_CODES = void 0;
const zod_1 = require("zod");
const contract_1 = require("../exports/contract");
const contract_2 = require("../files/contract");
const identifier_1 = require("../identifier");
const dashboard_1 = require("./dashboard");
const contract_3 = require("../tasks/contract");
const contract_4 = require("../worklogs/contract");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C27 · the Agent's wire. `docs/plan/agent.md`, R-24…R-27.                  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * One turn of the agent is **a stream, not a response**, so the contract is a closed set of frames
 * rather than a body shape. CLAUDE.md §3 all the same: defined once, the server serialises with it
 * and the client infers from it.
 *
 * ⚠️⚠️ **NO FRAME CARRIES A SENTENCE MEANT FOR A HUMAN, and that is the property that keeps rule 7
 * satisfiable.** The server does not know the reader's locale — there is no `Accept-Language` in the
 * scope, and a locale on the wire would mean the API owning half the catalogue. So a frame carries
 * a **machine identifier** (`tasks_search`, `rate_limited`) and the client maps it to a key in
 * `catalogue.ts`, where HU and EN are one type and a missing string is a type error.
 *
 * ⚠️ **There is exactly ONE exception and C27.3 built it** — `proposal.summary`, the sentence
 * somebody is asked to approve, which must not be able to disagree with what will be written. It is
 * argued where it is declared and in `docs/plan/agent.md` §5. Nothing else in this file may grow a
 * sentence; the test of a new field is *"could the client compose this from a code?"*
 */
/* ── what can go wrong ───────────────────────────────────────────────────────────────────────── */
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ RULE 5 HAS NO IN-BAND FORM, AND THIS IS IT.                          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"Every non-2xx response is the same error envelope. Clients branch on `code`, never on
 * `message`."* — and a stream that has already written `200` cannot change its status. Everything
 * that fails **after the first byte** therefore arrives as an `error` frame carrying the same
 * `{ code, message }` shape the envelope carries, and the client branches on `code` exactly as it
 * does for a 4xx.
 *
 * ⚠️ **Without this an SDK failure surfaces as a truncated sentence**, which is indistinguishable
 * from an answer — the one outcome C17 built the empty screen to prevent. A half-written reply that
 * simply stops is a fabrication with better manners.
 *
 * ⚠️ **The same codes are used BEFORE the stream opens**, in the ordinary envelope, so a client has
 * one mapping rather than two. `agent_not_configured` on a `GET` and in a frame mean the same thing
 * and read the same string.
 */
exports.AGENT_ERROR_CODES = [
    /**
     * No `ANTHROPIC_API_KEY`. ⚠️ **Not a fault — a declared absence.** A self-hosted tenant may
     * legitimately have no key, and `agent-screen.tsx`'s notice is what this renders: the shell C17
     * built is this branch, unchanged.
     */
    'agent_not_configured',
    /** The workspace's plan does not include the agent. `AGENT_FLAG`, and rule 6 — the server check. */
    'feature_not_enabled',
    /** A per-member or per-workspace cap. C27.5 sets the numbers; the code exists from the start. */
    'rate_limited',
    /**
     * ⚠️ **`not_found`'s vocabulary, on purpose.** A conversation belongs to one member (0042), so
     * asking for somebody else's is the cross-boundary read rule 5 answers with `not_found` —
     * existence is itself information.
     */
    'conversation_not_found',
    /** The iteration or budget cap stopped the turn. The reply so far stands; the model was looping. */
    'turn_too_long',
    /** Anthropic answered with something we cannot use, or did not answer. Retryable. */
    'upstream_unavailable',
    /* ── C27.3, the confirmation gate's three refusals ───────────────────────────────────────── */
    /**
     * ⚠️ **`not_found`'s vocabulary again, and for the same reason.** A proposal belongs to one
     * member's conversation, so somebody else's is the cross-boundary read rule 5 answers with
     * *not found* — existence is itself information.
     */
    'proposal_not_found',
    /**
     * Fifteen minutes have passed.
     *
     * ⚠️⚠️ **ITS OWN CODE, BECAUSE THE SENTENCE HAS TO BE DIFFERENT.** *"That confirmation is too
     * old — ask again"* is actionable; *"not found"* would send somebody looking for a bug. The card
     * that offered it is still on screen, which is exactly when a person needs to be told why the
     * button no longer works.
     */
    'proposal_expired',
    /**
     * Already accepted, already declined, or being executed right now.
     *
     * ⚠️⚠️ **A DOUBLE-CLICK MUST NOT WRITE TWICE, AND THIS IS THE CODE THAT SAYS IT DID NOT.** R-25:
     * *"proposals expire and are single-use"*. The claim is atomic in Postgres; this is what the
     * second claimant is told.
     */
    'proposal_already_used',
    /* ── 29 Oct 2026 · a conversation renamed ────────────────────────────────────────────────── */
    /**
     * ⚠️ **Added under rule 8.** An MCP channel (`0122`) cannot be renamed: its title is the client's
     * own name AND the key `openChannel` finds the channel by, under a unique index. A renamed channel
     * would be abandoned — the client's next session opens a fresh one beside it — and a name another
     * of the member's clients already uses would collide. The history menu offers no rename on one, so
     * this is the server's half of a rule the screen already keeps.
     */
    'conversation_not_renamable',
];
/* ── conversations, as read ──────────────────────────────────────────────────────────────────── */
/**
 * ⚠️⚠️ **A CONVERSATION BELONGS TO A MEMBER, NOT TO A WORKSPACE, and this is stricter than this
 * project's usual scoping for a reason that no amount of workspace scoping covers.** A transcript
 * is a sequence of reads performed under **one member's** authority and trimmed by **their** field
 * rules. A colleague reading it would be reading the workspace through somebody else's
 * permissions — and both parties are legitimately in the workspace, so rule 3's two layers both
 * say yes. 0042's policy therefore keys on `current_member_id()` as well, the way `0030` does for
 * a favourite and `0025` for an inbox.
 *
 * There is no sharing feature. Adding one is a decision, not a feature.
 */
exports.agentConversationSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** `conversation_e9111099` — the public identifier `0194` mints beside the uuid; see `identifier.ts`. */
    identifier: (0, identifier_1.identifierSchema)('conversation'),
    /**
     * ⚠️ **Nullable, because a conversation is created before it has a subject.** The composer opens
     * on an empty thread; the title is derived from the first message once there is one. A default of
     * *"Új beszélgetés"* stored in the column would be a user-facing string in the database, which
     * rule 7 puts in the catalogue instead — so the absence travels and the client names it.
     */
    title: zod_1.z.string().nullable(),
    /**
     * ┌───────────────────────────────────────────────────────────────────────┐
     * │ 29 Oct 2026 · WHO OPENED IT. Added under rule 8.                      │
     * └───────────────────────────────────────────────────────────────────────┘
     *
     * `agent` is a thread somebody typed into, `mcp` a channel a program opened (`0122`), `dashboard`
     * a project dashboard's bar (`0222`). ⚠️ **On the wire for one reason: an MCP channel cannot be
     * renamed** — its title is the client's name and the key it is found by — so the history menu
     * needs to know not to offer it. `conversation_not_renamable` is the server's half.
     */
    origin: zod_1.z.enum(['agent', 'mcp', 'dashboard']),
    createdAt: zod_1.z.string(),
    /** ⚠️ The list is ordered by this, not by `createdAt`: a thread you replied to today is today's. */
    updatedAt: zod_1.z.string(),
});
/* ── the frames ──────────────────────────────────────────────────────────────────────────────── */
/**
 * ⚠️ **Every frame names its own type in a `type` field, and the union discriminates on it.** The
 * SSE `event:` name carries the same value, which is redundant and deliberate: `live.controller.ts`
 * names its frames (`change`, `resync`, `expired`) so a reader of `curl -N` can see what is
 * happening, and a stream whose every line says `event: message` is one nobody can debug by eye.
 */
/**
 * The assistant's turn has begun.
 *
 * ⚠️⚠️ **NO ID, AND THE ID IT OBVIOUSLY WANTED IS ON `message_stop` INSTEAD.** The first version
 * carried the id the message would be stored under, minted in Node before streaming — which is
 * wrong twice: `agent_messages.id` is a **uuid v7** and the whole thread index
 * (`(workspace_id, conversation_id, id)`, read `order by id asc`) depends on it being time-ordered,
 * so a `randomUUID()` v4 would silently shuffle a transcript. And there is nothing for the client to
 * do with an id before the row exists.
 *
 * ⚠️ **It is not payload-free by accident.** Its job is to say *"the turn has started"* so the
 * client can draw a pending bubble — which matters precisely because a summarised thinking block can
 * take a noticeable moment before its first token, and a composer that clears with nothing in its
 * place reads as a swallowed message.
 */
const messageStartFrame = zod_1.z.object({
    type: zod_1.z.literal('message_start'),
});
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THINKING IS SUMMARISED, AND THE DEFAULT WOULD SEND NOTHING.          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * On Opus 5 `thinking.display` defaults to `'omitted'`: the model thinks, the tokens are billed,
 * and the blocks arrive **with empty text**. A thinking indicator built against the default renders
 * a permanent blank pause and reads as a hang — with nothing logged and nothing thrown.
 * `agent.service.ts` sets `display: 'summarized'` and `docs/plan/agent.md` §16 files it as a trap.
 *
 * ⚠️ **The raw chain of thought is never returned by the API, on any model.** What crosses here is
 * a summary, and it is shown as one — the disclosure is labelled, not presented as the model's
 * inner monologue.
 */
const thinkingStartFrame = zod_1.z.object({ type: zod_1.z.literal('thinking_start') });
const thinkingDeltaFrame = zod_1.z.object({
    type: zod_1.z.literal('thinking_delta'),
    text: zod_1.z.string(),
});
const thinkingStopFrame = zod_1.z.object({ type: zod_1.z.literal('thinking_stop') });
const textDeltaFrame = zod_1.z.object({
    type: zod_1.z.literal('text_delta'),
    text: zod_1.z.string(),
});
/**
 * A tool is running.
 *
 * ⚠️ **`name` is the machine identifier and the client translates it.** See the file header. In
 * C27.1 nothing emits this — the registry is empty — and the frame exists anyway, because the
 * client's renderer and the contract are written together or they are written twice.
 */
const toolStartFrame = zod_1.z.object({
    type: zod_1.z.literal('tool_start'),
    toolCallId: zod_1.z.string(),
    name: zod_1.z.string(),
});
const toolEndFrame = zod_1.z.object({
    type: zod_1.z.literal('tool_end'),
    toolCallId: zod_1.z.string(),
    ok: zod_1.z.boolean(),
    /**
     * How many rows the tool returned, when that is a meaningful thing to say.
     *
     * ⚠️ Null rather than 0 for a tool that returns no list, so *"nothing found"* and *"not a list"*
     * are different states. A `0` rendered as *"0 feladat"* under a tool that answers a yes/no
     * question is a sentence about nothing.
     */
    count: zod_1.z.number().int().nonnegative().nullable(),
    /** ⚠️ A refusal's CODE, so the client can say which refusal. Never a message — see the header. */
    errorCode: zod_1.z.string().nullable(),
});
/**
 * ⚠️ **`stopReason` is passed through from the API rather than narrowed to a closed set.** Anthropic
 * adds them — `refusal` and `pause_turn` both arrived after this product line started — and a Zod
 * enum here would turn a new one into a validation failure at the end of an otherwise good turn.
 * The client branches on the two it acts on and renders the rest as "finished".
 */
const messageStopFrame = zod_1.z.object({
    type: zod_1.z.literal('message_stop'),
    /**
     * The id the turn was stored under.
     *
     * ⚠️ **Here rather than on `message_start` because this is the first moment it exists** — the row
     * is written before this frame is sent, and its id is the database's uuid v7. See the note on
     * `message_start`. A turn that fails sends `error` and no id, which is correct: nothing was
     * stored, so there is nothing to name.
     */
    messageId: zod_1.z.string(),
    stopReason: zod_1.z.string().nullable(),
    /**
     * What the turn cost. ⚠️ **On the wire from the first version, before anything reads it** — C27.5
     * builds the caps, and a token count that starts being recorded only when somebody needs it has
     * no history to answer the first question anybody asks.
     */
    usage: zod_1.z.object({
        inputTokens: zod_1.z.number().int().nonnegative(),
        outputTokens: zod_1.z.number().int().nonnegative(),
        /** ⚠️ The cache's own evidence. Zero across repeated turns means a silent invalidator. */
        cacheReadTokens: zod_1.z.number().int().nonnegative(),
        cacheWriteTokens: zod_1.z.number().int().nonnegative(),
    }),
});
/* ── structured data, which the TOOL emits and the model never authors ────────────────────────── */
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE ROWS ON A `data` FRAME CAME OUT OF A SERVICE, NOT OUT OF THE      │
 * │ MODEL. That is the whole design and the alternative is the trap.          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The ask C27 answers is *"listing tasks should not be a bulleted list — it should look like the
 * task list"*. There are two ways to do that and only one of them is honest:
 *
 *  * the model writes a markdown table, or emits JSON in its prose for the client to parse — and
 *    then **the model is the source of the rows.** It will drop a column, round an hour, invent a
 *    status name, or summarise five rows as four. That is the fabrication C17 refused, wearing a
 *    table's clothing;
 *  * the **tool** emits the rows it read, as the product's own wire type, on a frame of their own.
 *
 * So one tool call produces **two renderings of one result**, deliberately different: this frame,
 * carrying `Task[]` exactly as `/tasks` returns it — already permission-trimmed, already
 * field-trimmed, with `statusName`, `statusColor` and `trackedHours` resolved server-side — and a
 * compact text table for the model, which has no use for an avatar URL and would pay for it on
 * every subsequent turn. `tools/render.ts` holds the second one.
 *
 * ⚠️ **The client renders these with the product's OWN components** — `taskRows`/`taskColumns`, the
 * same functions the tasks screen calls. CLAUDE.md's frontend rule, applied literally: a second
 * chat-specific table would be a second place for every column, colour and formatter to drift, and
 * the drift would be invisible — the chat showing `8.0` where the list shows `8:00`.
 */
/**
 * What a `data` block is *about*. ⚠️ The client switches on this to choose a renderer, so it is a
 * closed set: an unknown kind is a version skew between the two halves of this file, and the client
 * drops the frame rather than guessing.
 */
exports.AGENT_DATA_KINDS = ['tasks', 'worklogs', 'worklog_overview'];
/**
 * ⚠️⚠️ **A CAP AND AN HONEST FOOTER, NEVER A SILENT TRUNCATION.** `state-rules.md` §5 is the rule
 * being applied — *a figure derived from a partial fetch is marked partial and never presented as a
 * total*. So a block that was cut states the **true** total beside what it shows, and the card says
 * *"25 of 312 · refine the question"* rather than trailing off.
 *
 * ⚠️ **`total` is NULLABLE, because for one of the three kinds nobody has counted.** The worklog
 * list pages with a cursor and `pagination.ts` explains why the envelope carries no count of its
 * own; a `0` or a guessed number there would be exactly the lie the rule above forbids. Null means
 * *not counted*, and the card says *"the first 25"* instead of a ratio.
 */
const readBlock = {
    /**
     * ⚠️ **When it was read, stamped by the server.** The route's `notLiveBecause` is an argument
     * about this field: a reply is a record of what was read at a moment, and a table that quietly
     * refreshed underneath the sentence describing it would make the sentence false.
     *
     * ⚠️⚠️ **A REHYDRATED CARD CARRIES THE MOMENT IT WAS RE-READ, NOT THE MOMENT OF THE REPLY, AND THAT
     * IS DELIBERATE (C27.4).** Reopening a conversation re-reads the stored ids through the ordinary
     * service — so the rows are current while the sentence above them is from yesterday. Stamping the
     * old time would present fresh figures as the ones the reply was written from, which is the one
     * thing this field exists to prevent. The reader sees a table read *now* under an answer written
     * *then*, which is true and is visibly so.
     */
    readAt: zod_1.z.string(),
    total: zod_1.z.number().int().nonnegative().nullable(),
    truncated: zod_1.z.boolean(),
    /**
     * ┌───────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ HOW MANY STORED ROWS DID NOT COME BACK. R-26's own sentence.      │
     * └───────────────────────────────────────────────────────────────────────┘
     *
     * *"The card says '2 of 5 rows are no longer visible to you' for whatever does not come back."* A
     * reopened conversation re-reads ids, and a member removed from a project in the meantime gets a
     * shorter table than the one the reply describes. Saying nothing would make the prose look wrong;
     * saying this makes it explicable.
     *
     * ⚠️ **Always `0` on a live block**, where nothing was re-read and nothing can be missing. Not
     * optional, so the client has one branch rather than two.
     */
    missing: zod_1.z.number().int().nonnegative(),
};
const taskListBlock = zod_1.z.object({
    kind: zod_1.z.literal('tasks'),
    /** ⚠️ `taskSchema`, unchanged — the same objects the tasks screen renders. See the note above. */
    items: zod_1.z.array(contract_3.taskSchema),
    ...readBlock,
});
const worklogListBlock = zod_1.z.object({
    kind: zod_1.z.literal('worklogs'),
    items: zod_1.z.array(contract_4.worklogSchema),
    ...readBlock,
});
/**
 * The aggregate `/time` renders, in a reply.
 *
 * ⚠️⚠️ **THIS IS THE TOOL THAT CLOSES THE LOOP WITH C17.** The mockup answers *"mennyi órám van
 * ezen a héten"* with a number nobody calculated. This carries the day strip, the week total and
 * the project breakdown from `WorklogService.overview` — summed in SQL because summing `numeric` in
 * JavaScript reintroduces floating-point drift on the figures somebody bills from, and formatted
 * server-side as `H:MM` so one screen cannot show `7.5` where the next shows `7:30`.
 *
 * ⚠️ **`subject` is a machine value, not a sentence.** *Whose* hours these are is the most important
 * thing on the card — a week total that is silently the whole workspace's is a wrong answer with a
 * chart under it — and the client says it in HU or EN from this enum. See the file header on why no
 * frame carries prose.
 */
const worklogOverviewBlock = zod_1.z.object({
    kind: zod_1.z.literal('worklog_overview'),
    overview: contract_4.worklogOverviewSchema,
    subject: zod_1.z.enum(['own', 'everyone']),
    granularity: zod_1.z.enum(['week', 'month']),
    readAt: zod_1.z.string(),
});
exports.agentDataBlockSchema = zod_1.z.discriminatedUnion('kind', [
    taskListBlock,
    worklogListBlock,
    worklogOverviewBlock,
]);
/**
 * One structured result, beside the prose that describes it.
 *
 * ⚠️ **It carries `toolCallId`, so a card belongs to a visible tool row** rather than floating in
 * the turn. Two searches in one turn produce two rows and two cards, and the transcript then shows
 * where each figure came from — which is the property that makes an answer checkable.
 */
const dataFrame = zod_1.z.object({
    type: zod_1.z.literal('data'),
    toolCallId: zod_1.z.string(),
    block: exports.agentDataBlockSchema,
});
/* ── an export the model worked out, for a person to run ─────────────────────────────────────── */
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ C48 · A FILTER THE MODEL RESOLVED. NOT A FILE, AND NOT A PERMISSION  │
 * │ TO MAKE ONE. R-34.                                                        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"Export the hours we logged on Acme last month"* is two hard parts and one easy one. The hard
 * parts are which domain and which range — `2026-08-01` to `2026-08-31`, `projectId` for the project
 * somebody called *Acme* — and a model is good at both. The easy part is producing the CSV, and
 * `POST /exports` has done that since C17: permission-trimmed with the same narrowing the list screen
 * uses, CSV injection neutered, CRLF and a BOM, and the row count deciding download-or-email.
 *
 * ⚠️⚠️ **SO THIS CARRIES THE FILTER AND NOTHING ELSE, AND PRESSING IT OPENS THE PRODUCT'S OWN EXPORT
 * DIALOG PRE-FILLED.** The person confirms in the surface they already know, and the request that
 * runs is the one their own form composed. That is the whole reason there is no proposal row here:
 * R-25 exists because a **write** must not happen on the model's authority, and an export writes
 * nothing — it reads what the caller may already read. What it does have is one side effect worth a
 * human press, and the dialog is where somebody presses: past `EXPORT_INLINE_LIMIT` the file is
 * **emailed**, and a model must not be able to send mail by inferring a date range.
 *
 * ⚠️ **`EXPORT_DOMAINS` whole, rather than a two-member subset of it.** Only `task` and `worklog`
 * have tools today (they are the dated domains, and the ones people name a range for), but the
 * client hands this straight to `useExport().open(domain)` — and a narrower enum here would buy a
 * cast at that call site in exchange for an invented enum. The tools are the constraint.
 *
 * ⚠️⚠️ **THE RESOLVED NAMES TRAVEL WITH THE IDS, AND THAT IS NOT PROSE.** *"időbejegyzések · Acme ·
 * 2026-08-01 – 08-31"* has to be readable before somebody presses it — an offer showing a uuid is an
 * offer nobody can check — and a name is **data**, exactly as `statusName` and `projectName` are on
 * every block above. The client still composes the sentence, in its own locale, from these parts.
 *
 * ⚠️ **A name that cannot be resolved means the offer is DROPPED, not shown nameless.** See
 * `transcript.service.ts`: on reload the ids are re-read through the ordinary services, so a project
 * the reader has since lost access to takes its offer with it rather than leaving them a button that
 * exports somebody else's narrowing.
 */
exports.agentExportOfferSchema = zod_1.z.object({
    domain: zod_1.z.enum(contract_1.EXPORT_DOMAINS),
    /**
     * ⚠️⚠️ **WHICH DATE THESE BOUND IS THE DOMAIN'S BUSINESS AND IT IS NOT THE SAME ONE.** A worklog
     * export filters `worked_on`; a task export filters **`due_on`** (`export.service.ts`, the same
     * column C15's task reports use). So *"August's tasks"* means *due* in August, and the tool
     * descriptions say so — a range that silently means a different date is a wrong file that looks
     * right.
     */
    from: zod_1.z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable(),
    to: zod_1.z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable(),
    projectId: zod_1.z.uuid().nullable(),
    projectName: zod_1.z.string().nullable(),
    /**
     * ⚠️ **`worklog` only, and the export service is why**: a contract's hours are its line items'
     * tasks UNIONED with its collector task (P2-10), which is a worklog filter. A task export ignores
     * it rather than half-supporting it — the same rule `dataExportRequestSchema` states.
     */
    contractId: zod_1.z.uuid().nullable(),
    contractCode: zod_1.z.string().nullable(),
    /**
     * ⚠️⚠️ **HONOURED ONLY WHERE THE CALLER'S READ IS ALREADY WORKSPACE-WIDE**, and that is the export
     * service's own rule rather than something this offer can grant: with `worklog.read` at `own` the
     * filter is forced to the caller and this is ignored, because *"export Anna's hours"* from somebody
     * who may only see their own must not widen anything. The offer may carry it; only the trim decides.
     */
    memberId: zod_1.z.uuid().nullable(),
    memberName: zod_1.z.string().nullable(),
});
/**
 * ⚠️ **Its own frame rather than a `data` block, for one concrete reason: `data-cards.ts`.** That flag
 * switches off *"the tables that follow a read"* — its own words — and it does it at the frame level.
 * An offer riding on `data` would have gone off with them, and making the flag per-kind would cost
 * R-26 the property that makes it trustworthy: *"to turn it back on, return `true`. Nothing else."*
 *
 * ⚠️ **`toolCallId`, like `data`**, so the offer belongs to a visible tool row rather than floating in
 * the turn — which is what lets a reader see which question produced it.
 */
const exportOfferFrame = zod_1.z.object({
    type: zod_1.z.literal('export_offer'),
    toolCallId: zod_1.z.string(),
    offer: exports.agentExportOfferSchema,
});
/* ── a write, proposed and not performed ─────────────────────────────────────────────────────── */
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ A WRITE TOOL DOES NOT WRITE. It proposes, and the turn ends here.     │
 * │ R-25, and `docs/plan/agent.md` §5.                                        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The mockup's own line under the composer read *"Írási művelet előtt megerősítést kér"* — it asks
 * for confirmation before a write. C17 deleted that line for describing an agent that did not exist.
 * This is the shape that makes it true, and the important half is that **the gate is the server's**:
 * the tool persisted a row and stopped, and only `POST /agent/proposals/:id/accept` performs the
 * write. A confirmation the client could skip would be decoration (rule 6).
 *
 * ⚠️ **The turn is over when this arrives.** `message_stop` follows it — the transcript is complete
 * and valid, with the tool call answered by *"proposed, awaiting confirmation"* — and the model's
 * account of what happened arrives on the **next** stream, the one the accept endpoint returns.
 */
/**
 * What kind of value a field holds, and therefore what control the card draws.
 *
 * ⚠️ **Four, and none of them is a picker.** `hours` and `date` exist because the product has one
 * notation for each and the card must not invent a second; `text` is a title or a name; `note` is the
 * one multi-line field. An id-valued field is `kind: null` and read-only — a status picker needs the
 * workspace's vocabulary in the browser, which is a bigger change than this one.
 */
exports.AGENT_FIELD_KINDS = ['text', 'note', 'hours', 'date'];
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ C46 · WHAT MAY BE EDITED, AND WHAT IS THE MODEL'S OWN GUESS.        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * One field of one row of a confirmation card. `kind` is what input to draw **and** the permission to
 * draw one at all: `null` means *render this as text*, which is how an id-valued field — a status, an
 * assignee, a line item — stays visible without becoming a picker this chunk does not build.
 *
 * ⚠️⚠️ **`kind` IS ALSO THE SERVER'S ALLOWLIST, AND THAT IS THE SECURITY-RELEVANT HALF.** `revise`
 * accepts a value only for a field whose stored `kind` is non-null, so a client cannot patch
 * `taskKey` and retarget the write. The card and the gate read the same column.
 *
 * ⚠️ **`derived` is `agent_plan_1.md` §0.4** — *"kitalált érték jelölve van"*, with the reasoning on
 * hover. Non-null means the model supplied this value rather than the person, and the sentence is
 * **why**: *"a hasonló feladatok mediánja"*, *"a fájl 7. sora"*, *"három munkanapra elosztva"*. A
 * suggestion that is visibly a suggestion is not the fabrication the preamble forbids; an unmarked
 * one is.
 */
exports.agentProposalFieldSchema = zod_1.z.object({
    /** The property this field writes on the tool input's row. Never shown. */
    key: zod_1.z.string(),
    /** ⚠️ Already in the reader's language — the same argued exception `summary` is. */
    label: zod_1.z.string(),
    /**
     * The value, formatted as the product writes it: `8:00` for hours, `2026-09-01` for a date.
     *
     * ⚠️ **Without the `~`.** The marker is a rendering of `derived`, not part of the value — it goes
     * into an input box, and a person who edits a field prefixed with a tilde would send the tilde.
     */
    value: zod_1.z.string(),
    /** ⚠️ Null means read-only — see the header. */
    kind: zod_1.z.enum(exports.AGENT_FIELD_KINDS).nullable(),
    derived: zod_1.z.string().nullable(),
});
exports.agentProposalRowSchema = zod_1.z.object({
    /** What this row is, as a person would name it — *"HSO-131 · 2026. augusztus 24."* */
    label: zod_1.z.string(),
    fields: zod_1.z.array(exports.agentProposalFieldSchema),
});
exports.agentProposalSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** `proposal_e9111099` — the public identifier `0194` mints beside the uuid; see `identifier.ts`. */
    identifier: (0, identifier_1.identifierSchema)('proposal'),
    /**
     * ⚠️ **The write's machine name** — `worklogs_log`, `tasks_update` — which the client turns into a
     * heading. See the file header: no frame carries prose.
     */
    tool: zod_1.z.string(),
    /**
     * ┌───────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ THE ONE EXCEPTION TO THIS FILE'S HEADER, AND IT IS ARGUED.        │
     * └───────────────────────────────────────────────────────────────────────┘
     *
     * Every other frame carries a machine identifier because the server does not know the reader's
     * locale. This one carries a **sentence**, composed by the API from per-tool templates in the
     * member's own locale — *"8:00 óra a HSO-131 feladatra, 2026-09-01"*.
     *
     * The alternative is the client re-deriving that sentence from a generic payload, which is a
     * second implementation of **the one string in the product that must not be able to disagree with
     * what will be written**. Somebody is being asked to approve this; the words they read and the
     * row that gets inserted have to come from the same place. `docs/plan/agent.md` §5.
     */
    summary: zod_1.z.string(),
    /**
     * ⚠️ **Fifteen minutes from the proposal, and it is on the wire so the card can say so.** R-25: a
     * pending write from yesterday's conversation, accepted by a mis-click, writes hours onto a month
     * somebody has already reported.
     */
    expiresAt: zod_1.z.string(),
    /**
     * ┌───────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ ONE LINE PER ROW THE WRITE WILL PRODUCE, AND THE SUMMARY ABOVE    │
     * │ IS THEN A HEADLINE RATHER THAN THE WHOLE CARD. C27.6.                 │
     * └───────────────────────────────────────────────────────────────────────┘
     *
     * Every write tool takes a **list** — *"log two hours to HSO-131 for each day last week"* is one
     * call, one proposal and one button, because R-25 permits one proposal per turn and seven
     * confirmations for one sentence somebody said is an obstacle rather than a gate.
     *
     * ⚠️⚠️ **A CARD READING *"5 BEJEGYZÉS"* AND NOTHING ELSE WOULD BE A BLANK CHEQUE.** §5's whole
     * argument is that the words somebody reads and the rows that get written come from the same
     * call. A count is not those words, so every day and every figure is here, in order, composed in
     * the same call and in the same locale as `summary` — the same argued exception, not a second one.
     *
     * ⚠️ **Empty for a single write, deliberately, rather than an array of one.** A proposal for one
     * worklog keeps C27.3's card unchanged: one sentence, no list. A list of one is a worse rendering
     * of the same fact, and treating the singular case as a batch would have turned every existing
     * card into one.
     */
    details: zod_1.z.array(zod_1.z.string()),
    /**
     * ┌───────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ C46 · THE FIELDS, SO THE CARD CAN BE CORRECTED RATHER THAN ONLY  │
     * │ READ. `agent_plan_1.md` §0.2.                                         │
     * └───────────────────────────────────────────────────────────────────────┘
     *
     * *"A widget mindig teljes. Nem »mentsem?«, hanem minden mező kitöltve, szerkeszthetően. A
     * felhasználó a widgetben javít, nem chatben."* And R-25 says the accept endpoint executes the
     * input **it** stored, never anything a client sends back. Both hold, because an edit does not
     * travel with the accept — it **re-proposes**: `POST /agent/proposals/:id/revise` merges the values
     * into the stored input, runs the tool's own `run()` over the result, and writes a new proposal
     * somebody still has to press.
     *
     * ⚠️⚠️ **COMPOSED BY THE SAME CALL AS `summary`, WHICH IS WHY IT IS HERE AND NOT DERIVED IN THE
     * BROWSER.** A client building a form from the tool's JSON Schema would be a second implementation
     * of *what is being proposed* — the seam §5 refuses — and it would have nowhere to put the derived
     * marker, because nothing in a schema knows which value the model inferred.
     *
     * ⚠️ **EMPTY for a card that is not editable, which today is every batch.** One field list is a
     * form somebody reads; fifty is a spreadsheet nobody checks. A batch keeps `details`, and the
     * derived values in it are marked with a `~` in the line itself.
     */
    rows: zod_1.z.array(exports.agentProposalRowSchema),
});
const proposalFrame = zod_1.z.object({
    type: zod_1.z.literal('proposal'),
    proposal: exports.agentProposalSchema,
});
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ WHAT A CONFIRMATION CARD CAN SAY, AS A CLOSED SET — including the     │
 * │ two states only the client ever produces.                                 │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Six, and the reason they live here rather than in the component is that C27.4 sends four of them
 * over the wire: a reopened conversation has to render a card whose fate was decided in another tab,
 * or last week. Two unions would drift the first time a state was added.
 *
 *  * `pending` — the two buttons;
 *  * `working` — a request is in flight. The server sends it for a proposal caught mid-execution;
 *    the client sets it the moment a button is pressed;
 *  * `approved` — the person said yes and the accept went through. ⚠️⚠️ **NOT *"recorded"*: the write
 *    can still have been refused (a closed month, a stale row), and the only thing that knows which is
 *    the reply underneath the card.** A card claiming the row exists would be a fabricated
 *    confirmation;
 *  * `declined` — dismissed;
 *  * `expired` — fifteen minutes passed before anybody pressed it;
 *  * `used` — already answered. ⚠️ Client-only: the server distinguishes *how* it was answered, and
 *    this is what a refused accept leaves behind when the client did not see the answer happen.
 */
exports.AGENT_PROPOSAL_STATES = [
    'pending',
    'working',
    'approved',
    'declined',
    'expired',
    'used',
];
/** A proposal as a REOPENED transcript carries it: the offer, plus what became of it. */
exports.storedAgentProposalSchema = exports.agentProposalSchema.extend({
    state: zod_1.z.enum(exports.AGENT_PROPOSAL_STATES),
});
const errorFrame = zod_1.z.object({
    type: zod_1.z.literal('error'),
    code: zod_1.z.enum(exports.AGENT_ERROR_CODES),
    /**
     * ⚠️⚠️ **FOR A LOG, NOT FOR A SCREEN, and the client must not render it.** It exists because
     * `upstream_unavailable` with no detail is unactionable for whoever is on call, and it is
     * English, untranslated, and may quote a third party. The reader sees the catalogue string for
     * `code`. Rule 5's *"clients branch on `code`, never on `message`"* is the same instruction.
     */
    message: zod_1.z.string(),
});
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 29 Oct 2026 · THE THREAD'S TITLE CHANGED. Added under rule 8.             │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Sent twice in a conversation's life, at most: right after `message_start` on the first question,
 * carrying the provisional title (the first line of what was asked), and once after `message_stop`
 * when the model's summary of that question has replaced it. `0246` argues the order.
 *
 * ⚠️⚠️ **AFTER `message_stop`, AND A CLIENT MUST KEEP READING.** The summary is a second model call,
 * started with the turn and applied only once an answer exists; sending it before `message_stop`
 * would hold the composer busy for a title. So the turn is over when `message_stop` arrives, and
 * this frame may still follow it before the stream closes.
 *
 * ⚠️ **The title is DATA — the model's words or the person's — not a catalogue string**, exactly as
 * the column is (`0042`). Rule 7 is about our words.
 */
const conversationTitleFrame = zod_1.z.object({
    type: zod_1.z.literal('conversation_title'),
    title: zod_1.z.string().min(1).max(120),
});
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 29 Oct 2026 · THE CONVERSATION BEFORE THIS ANSWER WAS SUMMARISED.         │
 * │ Added under rule 8.                                                       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * A thread no longer stops at forty questions: once its history outgrows the room `agent.service.ts`
 * gives it, the API replaces the older part with a summary it writes itself (`transport.ts` argues
 * the mechanism). The model then knows the start of the thread only as that summary, while the
 * reader can still scroll up to every word of it, so the screen says where it happened. Somebody
 * asking about *"the third row of the first table"* should be able to see why the answer is vaguer
 * than the table.
 *
 * ⚠️ **Carries nothing, and is sent at most once per turn**, after the API call that compacted: a
 * mid-turn summary arrives between two tool rounds, a final-round one just before `message_stop`.
 * The stored turn says the same thing as `agentMessageSchema.compacted`, so a reopened thread shows
 * the line where the live one did.
 */
const compactedFrame = zod_1.z.object({ type: zod_1.z.literal('compacted') });
exports.agentFrameSchema = zod_1.z.discriminatedUnion('type', [
    messageStartFrame,
    thinkingStartFrame,
    thinkingDeltaFrame,
    thinkingStopFrame,
    textDeltaFrame,
    toolStartFrame,
    toolEndFrame,
    dataFrame,
    /* ⚠️ C48 · a filter the model resolved, for a person to press. Its own frame, not a `data` block —
       the schema above argues why (`data-cards.ts` switches blocks off at the frame level). */
    exportOfferFrame,
    proposalFrame,
    messageStopFrame,
    /* 29 Oct 2026 · the provisional title, and the summary that replaces it. */
    conversationTitleFrame,
    /* 29 Oct 2026 · the older part of the thread was summarised during this turn. */
    compactedFrame,
    errorFrame,
]);
/**
 * The frame names, as a value.
 *
 * ⚠️ `LIVE_FRAMES` exists for the same reason and `live/contract.ts` states it: a test that asserts
 * the client handles every frame needs the list, and deriving it from the union at runtime is not
 * something Zod hands over cleanly. Kept beside the union so the two are edited together.
 */
exports.AGENT_FRAMES = [
    'message_start',
    'thinking_start',
    'thinking_delta',
    'thinking_stop',
    'text_delta',
    'tool_start',
    'tool_end',
    'data',
    'export_offer',
    'proposal',
    'message_stop',
    'conversation_title',
    'compacted',
    'error',
];
/**
 * One stored turn, as the client reads it back.
 *
 * ⚠️⚠️ **THIS IS NOT WHAT THE MODEL IS SENT.** `agent_messages.content` holds the Anthropic content
 * blocks verbatim — thinking blocks included, because they must be echoed back unchanged on the
 * same model — and that shape is the SDK's, not ours. What crosses to the browser is the reader's
 * view: the prose, and (from C27.4) the pointers to what was read. Putting the SDK's block union on
 * our wire would make every client change a function of somebody else's release notes.
 */
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ C49.1 · WHICH TOOLS A STORED TURN RAN. R-26's SHAPE, FOR THE ONE     │
 * │ PART OF A TURN THAT WAS ONLY EVER A FRAME.                               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `debt.md` carried this since C27.4: a reopened turn brought back its cards and its confirmation
 * and **not** *"Feladatok keresése — 12 feladat"*, because which tools ran was never stored. The row
 * also named the wrong fix and said so: *"deriving one from a card would be the client asserting a
 * read it has no record of"*. This is the right one — the tool's own name and what it returned,
 * written down when it happened.
 *
 * ⚠️ **`name`, never a sentence.** The catalogue holds the label and the server does not know the
 * reader's locale — the same rule the live `tool_end` frame follows, and the reason `stepLabel` is
 * a client function.
 *
 * ⚠️ **No `state`, because a stored run is always finished.** `running` cannot survive a reload, and
 * `done` versus `failed` is `errorCode === null` — a stored state would be a third source of a fact
 * two fields already carry.
 */
exports.agentToolRunSchema = zod_1.z.object({
    name: zod_1.z.string(),
    /** ⚠️ Null for a tool that answers no list — `tool_end.count`'s rule, unchanged. */
    count: zod_1.z.number().int().nonnegative().nullable(),
    /**
     * Which refusal, when it was refused.
     *
     * ⚠️⚠️ **A FAILED RUN IS STORED, AND LEAVING IT OUT WOULD HAVE BEEN THE ORIGINAL BUG AGAIN.** A
     * turn whose read was refused says so live; a reopened turn that showed only the successes would
     * be quietly claiming a cleaner history than it had — and `one_at_a_time`, which is a rule rather
     * than a fault, would vanish along with the explanation for a card the reader can see.
     */
    errorCode: zod_1.z.string().nullable(),
});
exports.agentMessageSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** `message_e9111099` — the public identifier `0194` mints beside the uuid; see `identifier.ts`. */
    identifier: (0, identifier_1.identifierSchema)('message'),
    role: zod_1.z.enum(['user', 'assistant']),
    /** The prose. ⚠️ Plain text — the model is told to write prose, and nothing renders markup yet. */
    text: zod_1.z.string(),
    /**
     * The summarised reasoning, when there was any and it was kept.
     *
     * ⚠️ **Stored, so reopening a conversation is not a lesser version of watching it happen.** It is
     * collapsed by default on both paths; a disclosure that exists live and vanishes on reload is a
     * feature that appears to have broken.
     */
    thinking: zod_1.z.string().nullable(),
    /**
     * ┌───────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ THE TABLES THIS TURN READ, RE-READ THROUGH THE ORDINARY PATH.     │
     * │ R-26, and C27.4 is where it arrives.                                  │
     * └───────────────────────────────────────────────────────────────────────┘
     *
     * The transcript stores `{ kind, ids, readAt }` and never the rows (0046), so what crosses here has
     * been through RLS, the check service and the field rules **again**, on this request. A row that
     * has become invisible to this member since the reply was written is simply absent, and `missing`
     * counts it.
     *
     * ⚠️ **Empty for every turn that read nothing**, which is most of them.
     */
    blocks: zod_1.z.array(exports.agentDataBlockSchema),
    /**
     * ┌───────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ C48 · THE EXPORTS THIS TURN OFFERED, RE-RESOLVED. R-34.          │
     * └───────────────────────────────────────────────────────────────────────┘
     *
     * ⚠️ **Not switched off with the blocks above.** `data-cards.ts` governs *"the tables that follow
     * a read"* — its own words — and an offer is a control, in the same category as the confirmation
     * card that flag also leaves alone. A reopened conversation whose reply says *"the export is ready
     * to press"* has to still have something to press.
     *
     * ⚠️ **Empty is the ordinary case**, so an array rather than a nullable: almost no turn offers one,
     * and *"offered none"* and *"was not the kind of turn that offers"* need no distinction on the wire.
     */
    offers: zod_1.z.array(exports.agentExportOfferSchema),
    /**
     * ┌───────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ C49.1 · THE TOOLS THIS TURN RAN, IN ORDER.                       │
     * └───────────────────────────────────────────────────────────────────────┘
     *
     * ⚠️ **Not switched off with `blocks`**, and for the offer's reason rather than by analogy with
     * it: `data-cards.ts` governs *"the tables that follow a read"*, and a tool row is not a table —
     * it is the statement that a read happened, which is the thing C27.7 left the prose to carry
     * alone. So a reopened turn now shows the same rows the live stream showed, tables or no tables.
     *
     * ⚠️ **Empty for a turn that answered from the prompt**, and empty for every turn stored before
     * C49.1 — an old thread reads exactly as it did yesterday rather than acquiring a wrong history.
     */
    tools: zod_1.z.array(exports.agentToolRunSchema),
    /**
     * The write this turn offered, if it offered one, and what became of it.
     *
     * ⚠️ **A pending one is still actionable** — the buttons work on a reopened thread, because the row
     * is the gate and it does not care which tab is looking at it. An expired one says so.
     */
    proposal: exports.storedAgentProposalSchema.nullable(),
    /**
     * 29 Oct 2026 · whether the thread before this answer was summarised while it was written — the
     * stored half of the `compacted` frame. ⚠️ Added under rule 8. False on every turn stored before
     * it, which is true of them: nothing was summarised while the forty-question cap stood.
     */
    compacted: zod_1.z.boolean(),
    createdAt: zod_1.z.string(),
});
exports.agentConversationDetailSchema = exports.agentConversationSchema.extend({
    messages: zod_1.z.array(exports.agentMessageSchema),
});
/* ── what the client sends ───────────────────────────────────────────────────────────────────── */
/**
 * ⚠️ **The cap is 4000 characters, and it is a refusal rather than a truncation.** A composer that
 * silently drops the end of a pasted specification answers a question nobody asked. The number is
 * roughly two screens of typing — well past any real question, and small enough that it cannot be
 * the thing that fills a context window.
 */
exports.AGENT_MESSAGE_MAX_CHARS = 4000;
exports.sendAgentMessageRequestSchema = zod_1.z.object({
    text: zod_1.z.string().trim().min(1).max(exports.AGENT_MESSAGE_MAX_CHARS),
    /**
     * 19 Oct 2026 · what a dashboard thread's asker is looking at — `dashboard.ts`. Ignored on a
     * thread that is not a dashboard's: the Agent page has no view to describe.
     */
    view: dashboard_1.agentDashboardViewSchema.optional(),
});
/**
 * ⚠️ 19 Oct 2026 · `projectId` opens a DASHBOARD thread (`0222`, AG-1): every tool pinned to that
 * project, and the thread kept out of the Agent page's history. Absent is an ordinary thread.
 */
exports.createAgentConversationRequestSchema = zod_1.z.object({
    projectId: zod_1.z.uuid().optional(),
});
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 29 Oct 2026 · A CONVERSATION RENAMED. `PATCH /agent/conversations/:id`.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **Trimmed, then 1–120 characters — the column's own check (`0042`)**, so a name the column
 * would refuse is a `validation_failed` here rather than a constraint error behind a 500.
 *
 * ⚠️⚠️ **NO `version`, AND THAT DEPARTS FROM CLAUDE.md §3's STALE-WRITE RULE — ARGUED, NOT
 * OVERLOOKED.** `0042` kept the column for this rename. But `touch_row()` bumps it on every update,
 * and this row is updated three times a turn for bookkeeping (`updated_at`) — so a version read when
 * the menu opened is stale after almost any activity, and a rename would be refused for the thread
 * having been used. The row has one owner (`0042`), so two people cannot race over it, and the one
 * automated writer — the summary — yields to a person through `title_provisional` (`0246`). Last
 * write wins.
 */
exports.renameAgentConversationRequestSchema = zod_1.z.object({
    title: zod_1.z.string().trim().min(1).max(120),
});
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ HOW MANY ROWS ONE CONFIRMATION MAY CARRY. C27.6.                     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Every write tool takes a list, and this is where the list stops. Three separate things it bounds,
 * and the smallest of them is the one that set the number:
 *
 *  * **the card somebody actually reads.** Fifty lines is a long card and a person can still check
 *    it; five hundred is a scroll bar with a button under it, which is a confirmation in form only;
 *  * **the accept request.** One row is one transaction (see `tools/tool.ts`), so a batch is
 *    `n` round trips inside one HTTP request — the same cost `IMPORT_SYNC_LIMIT` exists to bound,
 *    at a fortieth of the size because nobody is watching a progress bar here;
 *  * **the model's own arithmetic.** A month of daily entries is 23 and two months is 46, which is
 *    the real shape of the ask: *"log two hours a day for every workday in August"*.
 *
 * ⚠️⚠️ **PAST THIS, THE ANSWER IS THE IMPORT WIZARD (C17), NOT A BIGGER NUMBER.** A file of two
 * thousand worklogs is a thing this product already does properly — parsed, per-row reported,
 * `IMPORT_SYNC_LIMIT` at 2,000 — and asking a model to retype it into tool arguments would be
 * slower, dearer and less accurate than the CSV path that exists. The tool descriptions say so, so
 * the model can say so.
 *
 * ⚠️ **Shared rather than declared in the tool**, because three places must agree on it: the Zod
 * schema that refuses a longer list, the sentence the model reads in the tool description, and the
 * test that checks the refusal.
 */
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ C46 · A CORRECTION, AND IT IS NOT A WRITE REQUEST.                  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The values a person changed in the card, keyed by the field's `key`. What comes back is a **new
 * proposal**, so the worst a client can do with this endpoint is produce a differently worded card
 * that somebody still has to press — R-25's *"the accept endpoint executes the stored input"* is
 * untouched.
 *
 * Three properties, all server-side:
 *
 *  * **a key the stored form did not declare EDITABLE is refused**, so `taskKey` cannot be patched
 *    and the write cannot be retargeted;
 *  * **the merged input goes through the tool's own `run()`**, so the value is validated by the same
 *    schema, resolved by the same lookups and described by the same sentence as the original offer;
 *  * **the offer being corrected becomes `revised`**, which is terminal — otherwise a tab holding the
 *    old card could still accept the figure somebody had just corrected.
 *
 * ⚠️ **`null` clears a field**, which is a real request — removing a note, clearing a date — and is
 * why the value is nullable rather than an empty string meaning two things.
 */
exports.reviseAgentProposalRequestSchema = zod_1.z.object({
    values: zod_1.z.record(zod_1.z.string(), zod_1.z.string().max(2000).nullable()),
});
/* ── C47: the files somebody handed the Agent ────────────────────────────────────────────────── */
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ C47 · ONE OF THIS CONVERSATION'S FILES, PLUS THE ONE THING C43 HAD  │
 * │ NO WAY TO SAY: WHICH MESSAGE IT WENT WITH.                               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **`fileAttachmentSchema` EXTENDED, NOT A SECOND SHAPE.** The client's download, its lightbox
 * and its size formatting all take a `FileAttachment`; a parallel `AgentFile` with the same six
 * fields would be a second place for `inline` to be decided and the one that forgets the closed
 * list. What the Agent adds is a pointer, so a pointer is what this adds.
 *
 * ⚠️⚠️ **`null` MEANS *attached, not yet sent*, AND THE SCREEN LEANS ON IT** — those are the files
 * in the strip above the composer, the ones that will go with the next question. A file already
 * bound renders as a chip above the message it arrived with. `0095` argues why the binding is a
 * stored fact rather than an inference from two timestamps.
 */
exports.agentConversationFileSchema = contract_2.fileAttachmentSchema.extend({
    messageId: zod_1.z.string().nullable(),
});
exports.agentConversationFileListSchema = zod_1.z.object({
    data: zod_1.z.array(exports.agentConversationFileSchema),
});
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ C47 · ONE FILE IN THE MEMBER'S OWN LIBRARY — EVERY THREAD, NOT      │
 * │ THIS ONE.                                                                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"I sent that spreadsheet to the Agent last month"* is a sentence about a person, not about a
 * thread, and before this it had no answer: a file existed only inside the conversation it was
 * uploaded to. So the library is **per member**, which is the same scope a conversation has
 * (`0042`) — and deliberately NOT per user across workspaces, because the bytes live under a tenant
 * (`blobDomainPath`) and a library spanning two of them would hand one tenant's spreadsheet to
 * another.
 *
 * ⚠️ **`conversationTitle` is nullable for the reason `agentConversationSchema.title` is**: a
 * conversation exists before it has a subject, so a file attached to a brand-new thread has no
 * thread name to show yet, and the client names the absence.
 *
 * ⚠️ **`id` IS A LINK ID AND THE NEWEST ONE.** The same file sent to four threads is four rows in
 * `file_links` over one blob; the library shows it once, and the id it carries is the most recent of
 * those — good for a download, and good for the re-attach, which needs a readable link rather than a
 * blob.
 */
exports.agentLibraryFileSchema = contract_2.fileAttachmentSchema.extend({
    conversationId: zod_1.z.string(),
    conversationTitle: zod_1.z.string().nullable(),
});
exports.agentLibraryFileListSchema = zod_1.z.object({ data: zod_1.z.array(exports.agentLibraryFileSchema) });
/**
 * How many files the library returns.
 *
 * ⚠️ **A cap and not a page, because the widget is a browse and not a report.** Somebody looking for
 * the file they sent last week finds it near the top of a list ordered newest-first; a member with
 * four hundred spreadsheets does not need all four hundred in a panel, and cursor pagination inside
 * an overlay is a scroll listener and an empty state to serve a case nobody has yet. `debt.md`
 * carries the row so the absence is a decision rather than an oversight.
 */
exports.AGENT_LIBRARY_LIMIT = 100;
/**
 * Sending a file the member already has to the thread they are in.
 *
 * ⚠️⚠️ **A LINK ID, NEVER A FILENAME AND NEVER A HASH.** The id names one row the caller's own
 * authority can already reach, which is what lets the server re-check it — `file.service.ts` reads
 * that link through the caller's scope and refuses anything that is not their own agent file. A
 * filename would be a request to guess, and a content hash on the wire is an existence oracle over
 * every blob in the tenant.
 */
exports.attachAgentFileRequestSchema = zod_1.z.object({ fileId: zod_1.z.uuid() });
exports.AGENT_BATCH_LIMIT = 50;
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ R-36 · A DELETE CARD IS SHORTER THAN EVERY OTHER CARD, AND THE      │
 * │ NUMBER COMES OUT OF THE CARD'S OWN HEIGHT.                               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `agent-proposal-card.tsx` renders `details` in a `max-h-[15rem]` list — about ten lines before it
 * scrolls. For every other write that is a presentation detail: a batch of fifty corrections that
 * somebody skims is fifty rows they can fix afterwards. For a delete there is no afterwards, so the
 * limit is the number of lines a person can see **without scrolling**, and past it they would be
 * approving rows they have not read.
 *
 * ⚠️ **One number for both delete tools rather than one each.** The argument is about the card, and
 * both tools draw the same card; a person who wants eleven can ask twice, which is one more sentence
 * and two cards they have both actually looked at.
 */
exports.AGENT_DELETE_LIMIT = 10;
//# sourceMappingURL=contract.js.map